import { isRecoverableAuthErrorReason } from './auth-error-reasons';
import type { AuthErrorReason } from './auth-error-reasons';

/**
 * One-shot marker set by /auth/error before it restarts a lost-session login
 * (FFH-2636). A second arrival carrying the marker renders the error screen
 * instead of bouncing the user through Universal Login again.
 *
 * Separate from the callback hook's httpOnly `auth_callback_retry` marker
 * (FFH-2018): these arrivals never pass through /auth/callback, the page has
 * to set and read the marker itself, and a client-readable cookie is the
 * only way to do that.
 */
export const AUTH_ERROR_RETRY_MARKER_COOKIE_NAME = 'auth_error_retry';

// A successful callback clears the marker, so the TTL only has to outlast a
// failed attempt: long enough that an interactive Universal Login (password,
// MFA) cannot outrun it and re-arm the restart, short enough that an
// abandoned attempt does not suppress the next sign-in for long.
const RETRY_MARKER_MAX_AGE_SECONDS = 600;

// Same path as the callback marker: the cookie only needs to travel on the
// auth routes, and keeping it off the rest of the site avoids adding to the
// cookie-jar pressure that loses the transaction cookie in the first place.
export const AUTH_ERROR_RETRY_MARKER_COOKIE_PATH = '/auth';

const COOKIE_SEPARATOR = /;\s*/;

export const hasAuthErrorRetryMarker = (cookieHeader: string): boolean =>
  cookieHeader
    .split(COOKIE_SEPARATOR)
    .some((cookie) =>
      cookie.startsWith(`${AUTH_ERROR_RETRY_MARKER_COOKIE_NAME}=`)
    );

export const buildAuthErrorRetryMarkerCookie = (isSecure: boolean): string =>
  [
    `${AUTH_ERROR_RETRY_MARKER_COOKIE_NAME}=1`,
    `Path=${AUTH_ERROR_RETRY_MARKER_COOKIE_PATH}`,
    `Max-Age=${RETRY_MARKER_MAX_AGE_SECONDS}`,
    'SameSite=Lax',
    ...(isSecure ? ['Secure'] : [])
  ].join('; ');

type AutoRestartInput = {
  reason: AuthErrorReason;
  hasRetryMarker: boolean;
  canPersistCookies: boolean;
};

/**
 * Restart the login only for a recoverable reason, only once, and only when
 * the browser will keep the marker: a browser that drops cookies would lose
 * the fresh transaction cookie too and loop through Universal Login.
 */
export const shouldAutoRestartLogin = ({
  reason,
  hasRetryMarker,
  canPersistCookies
}: AutoRestartInput): boolean =>
  isRecoverableAuthErrorReason(reason) && !hasRetryMarker && canPersistCookies;
